import { Component } from '@theme/component';

/**
 * @typedef {Object} Refs
 * @property {HTMLAnchorElement[]} navLinks - Anchor links in the sticky "Shop by" bar.
 * @property {HTMLElement[]} rows - Collection row sections the links target.
 * @property {HTMLElement} [navBar] - The sticky "Shop by" bar.
 * @property {HTMLElement} [navSentinel] - A zero-height marker just above the bar.
 * @property {HTMLElement[]} [rails] - The horizontally scrolling product rail of each row.
 */

/**
 * The unified shop page: a sticky "Shop by" bar whose links smooth-scroll to
 * collection rows on the same page, with the active link tracked from scroll
 * position. Navigation never leaves the page, so every /collections URL keeps
 * rendering the same content. Each row's rail gets previous/next arrows on
 * desktop once it holds more cards than fit.
 *
 * @extends {Component<Refs>}
 */
class ShopCollections extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {IntersectionObserver | undefined} */
  #stickObserver;

  /** @type {ResizeObserver | undefined} */
  #railObserver;

  /** @type {Set<Element>} */
  #visibleRows = new Set();

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.#watchStick();
    window.addEventListener('resize', this.#watchStick, { signal: this.#abortController.signal });

    this.#railObserver = new ResizeObserver((entries) => {
      for (const entry of entries) this.#syncRailNav(entry.target);
    });
    for (const rail of this.refs.rails ?? []) this.#railObserver.observe(rail);

    const rows = this.refs.rows ?? [];
    if (rows.length === 0) return;

    // A band around the vertical center of the viewport decides the active
    // row, which works no matter which element is the scroll container
    // (Horizon scrolls .page-wrapper on desktop, the window on mobile).
    this.#observer = new IntersectionObserver((entries) => this.#handleIntersections(entries), {
      rootMargin: '-35% 0px -55% 0px',
    });

    for (const row of rows) this.#observer.observe(row);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#stickObserver?.disconnect();
    this.#railObserver?.disconnect();
    this.#abortController.abort();
    this.#visibleRows.clear();
  }

  /**
   * Flags the bar as stuck once the sentinel above it crosses the line the
   * bar sticks at (its `top`, the header's height). The bar then docks at the
   * viewport top and pads by the same height, so its links never move.
   */
  #watchStick = () => {
    const { navBar, navSentinel } = this.refs;
    if (!(navBar instanceof HTMLElement) || !(navSentinel instanceof HTMLElement)) return;

    this.#stickObserver?.disconnect();
    navBar.removeAttribute('data-stuck');
    const stickAt = Math.max(0, Math.round(parseFloat(getComputedStyle(navBar).top) || 0));

    this.#stickObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const stuck = !entry.isIntersecting && entry.boundingClientRect.top < stickAt;
        if (stuck) navBar.setAttribute('data-stuck', 'true');
        else navBar.removeAttribute('data-stuck');
      },
      { rootMargin: `-${stickAt}px 0px 0px 0px`, threshold: 0 }
    );
    this.#stickObserver.observe(navSentinel);
  };

  /**
   * Smooth-scrolls to the row for a clicked nav link instead of letting the
   * browser jump, keeping the URL free of full navigations.
   *
   * @param {MouseEvent} event
   */
  handleNavClick = (event) => {
    // The delegated listener proxies `target` to the element carrying the
    // on:click attribute, so this is the anchor even for descendant hits.
    const link = event.target;
    if (!(link instanceof HTMLAnchorElement)) return;

    const targetId = link.hash.slice(1);
    const target = document.getElementById(targetId);
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.replaceState(null, '', link.hash);
    this.#setActiveLink(targetId);
  };

  /**
   * Pages a row's rail by whole cards. Next brings the first card that is cut
   * off (or out of view) to the start; previous goes back one viewful. Both
   * wrap around at the ends, so neither arrow is ever a dead control.
   *
   * @param {MouseEvent} event
   */
  scrollRail = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;
    const rail = button.closest('.shop-collections__row')?.querySelector('.shop-collections__rail');
    if (!(rail instanceof HTMLElement)) return;

    const rtl = getComputedStyle(rail).direction === 'rtl';
    const railBounds = rail.getBoundingClientRect();
    // The rail's inline padding (the page margin) doubles as its scroll padding
    const inset = parseFloat(getComputedStyle(rail).paddingInlineStart) || 0;
    const position = Math.abs(rail.scrollLeft);
    const maximum = rail.scrollWidth - rail.clientWidth;
    const view = rail.clientWidth - inset * 2;

    // Where each card starts and ends along the rail, in scroll coordinates
    const cards = [...rail.children].map((card) => {
      const bounds = card.getBoundingClientRect();
      const start = position + (rtl ? railBounds.right - bounds.right : bounds.left - railBounds.left) - inset;
      return { start, end: start + bounds.width };
    });

    let destination;
    if (Number(button.dataset.direction) > 0) {
      const cutOff = cards.find((card) => card.end > position + view + 2);
      destination = position >= maximum - 2 || !cutOff ? 0 : cutOff.start;
    } else {
      const firstInView = cards.find((card) => card.start >= position - view - 2);
      destination = position <= 2 ? maximum : firstInView?.start ?? 0;
    }

    destination = Math.max(0, Math.min(maximum, destination));
    rail.scrollTo({
      left: rtl ? -destination : destination,
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
  };

  /**
   * Shows a rail's arrows only while it has something to scroll to.
   *
   * @param {Element} rail
   */
  #syncRailNav(rail) {
    const nav = rail.closest('.shop-collections__row')?.querySelector('.shop-collections__rail-nav');
    if (nav instanceof HTMLElement) nav.hidden = rail.scrollWidth - rail.clientWidth <= 2;
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleIntersections(entries) {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        this.#visibleRows.add(entry.target);
      } else {
        this.#visibleRows.delete(entry.target);
      }
    }

    const rows = this.refs.rows ?? [];
    const topmost = rows.find((row) => this.#visibleRows.has(row));
    if (topmost) this.#setActiveLink(topmost.id);
  }

  /**
   * @param {string} rowId
   */
  #setActiveLink(rowId) {
    for (const link of this.refs.navLinks ?? []) {
      if (link.hash.slice(1) === rowId) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('shop-collections')) {
  customElements.define('shop-collections', ShopCollections);
}
